/**
 * Services-tier sites page — the sites of one plant inside a service project,
 * where a crew picks the site they are working and lands in its job history.
 * Same grid the customer tier shows, read services-first: the default columns
 * are Name · Location · Jobs · Status · Date · Last Crew, the leading filters
 * are job progress, photos, and crew (the site-health filters live behind the
 * More popover), and the stat cards count pending / complete / not-started
 * jobs and act as that filter. Per-row invoice shortcuts ride as extra
 * columns.
 *
 * Contains:
 * - SitesGridView — routed page for
 *   /services/customer/:customerId/project/:projectId/sitegroup/:siteGroupId
 */
import { ChangeCircle } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import { Box, Button, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import FileSaver from 'file-saver';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchJobSitesStats } from '@sophocles/api/sites';
import { useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import { selectRowMeta } from '@sophocles/data-grids/components/mobile/cardSections';
import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridQuickFilter from '@sophocles/data-grids/datasource/useGridQuickFilter';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import { formatGridDateTime } from '@sophocles/data-grids/grid/valueFormats';
import { deleteJob, generateChangeOrderInvoice, generateFixtureSwapInvoice } from '@sophocles/entities/Job';
import { fetchSiteGroup } from '@sophocles/entities/SiteGroup';
import { useListCrewsQuery } from '@sophocles/features/entities/crews';
import { setSelectedSiteGroup } from '@sophocles/features/projectServices';
import { setPlant } from '@sophocles/features/sites/slices/selectedPlant/actions';
import useCustomer from '@sophocles/hooks/useCustomer';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import { userPermCheck } from '@sophocles/utils';
import { FetchErrorState } from '@sophocles/views/services/components';
import SiteGrid from '@sophocles/views/sites/components/SiteGrid';

// Descriptor columns the services grid replaces with its own (the merged
// actions column below). Module-level for a stable identity.
const EXCLUDED_DESCRIPTOR_FIELDS = ['actions'];

// Crew-filter options fetch: active crews only (obsolete crews keep their
// history but take no new work). Module-level for a stable query-arg identity.
const CREW_OPTIONS_PARAMS = { 'filter[active]': 'true', sort: 'crew_number', limit: 500 };

// Small outlined buttons sharing the Select button's geometry, so a row's
// action cluster reads as one control set.
const actionButtonSx = { minWidth: 0, px: 0.75 };

/**
 * Reads the state of a site's most recent job.
 *
 * A site with no job at all is a distinct state from one whose job is
 * complete — a crew reads "None" as work not yet started, not work finished —
 * so the desktop column and the mobile card say the same three words.
 *
 * @param {boolean|null|undefined} latestPending - `latest_pending` off the row.
 * @param {Function} t - i18next translate.
 * @returns {string} The translated status label.
 */
function jobStatusLabel(latestPending, t) {
  if (latestPending == null) return t('projectServices.siteTable.statusNone');
  return latestPending ? t('projectServices.siteTable.statusPending') : t('projectServices.siteTable.statusComplete');
}

function SitesGridView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { customerId, projectId, siteGroupId } = useParams();
  const { user } = useUser();

  const canDelete = userPermCheck(user, 'odl_project.delete_projectjob');
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  const [statusFilter, setStatusFilter] = usePersistedState(useGridSessionKey('servicesSiteGrid', 'jobStatus'), null);
  const [photosFilter, setPhotosFilter] = usePersistedState(useGridSessionKey('servicesSiteGrid', 'photos'), null);
  const [crewFilter, setCrewFilter] = usePersistedState(useGridSessionKey('servicesSiteGrid', 'crew'), null);

  // Crew filter options. A user without crew visibility gets an error here,
  // not a list — the filter simply does not render for them.
  const { data: crewRows } = useListCrewsQuery(CREW_OPTIONS_PARAMS);
  const crewOptions = useMemo(
    () =>
      (crewRows ?? []).map(crew => ({
        value: String(crew.id),
        label: t('projectServices.dashboard.crewLabel', { crewNumber: crew.crew_number }),
      })),
    [crewRows, t],
  );

  // A flat route, so it seeds its own customer + plant context rather than
  // reading the scope a Layout would provide. SiteGrid takes plain JS.
  const {
    customer: customerMap,
    loading: customerLoading,
    error: customerError,
    reload: reloadCustomer,
  } = useCustomer(customerId);
  const [siteGroupMap, setSiteGroupMap] = useState(null);
  const [isPlantLoading, setIsPlantLoading] = useState(false);
  const [isPlantError, setIsPlantError] = useState(false);
  // Bumped by the error state's retry to re-run the plant seed effect.
  const [plantReloadToken, setPlantReloadToken] = useState(0);

  useEffect(() => {
    dispatch(setSelectedSiteGroup(siteGroupId));
    // Seed the selected-plant slice too — the breadcrumb trail reads the
    // plant name from it.
    dispatch(setPlant(siteGroupId));
    setIsPlantLoading(true);
    setIsPlantError(false);
    dispatch(fetchSiteGroup(siteGroupId))
      .then(res => setSiteGroupMap(res.siteGroups.first()))
      .catch(() => setIsPlantError(true))
      .finally(() => setIsPlantLoading(false));
  }, [dispatch, siteGroupId, plantReloadToken]);

  // One retry re-triggers both scope fetches; whichever succeeded is a cheap
  // cache-served no-op, so the button never needs to know which one failed.
  const handleScopeRetry = useCallback(() => {
    reloadCustomer();
    setPlantReloadToken(token => token + 1);
  }, [reloadCustomer]);

  // useCustomer reads a store slice shared across customers, so it reports the
  // previously selected one until the fetch lands. Withhold both objects until
  // the store ids match the route, or the grid lists the wrong scope's sites
  // on the first paint after switching.
  const isCustomerReady = customerMap?.get?.('id', '0').toString() === String(customerId);
  const customer = useMemo(() => (isCustomerReady ? customerMap.toJS() : null), [isCustomerReady, customerMap]);
  const isPlantReady = siteGroupMap?.get?.('id', '0').toString() === String(siteGroupId);
  const selectedPlant = useMemo(() => (isPlantReady ? siteGroupMap.toJS() : null), [isPlantReady, siteGroupMap]);

  // Customer-facing site term ("Sites"/"Structures") for the title and empty
  // state, resolved from the fetched customer (flat route — no scope Layout).
  const siteTerm = useSiteTerm(customer);

  // Rows lead into the site's job history within the services flow, not the
  // site dashboard this grid defaults to.
  const sitePath = useCallback(
    row => `/services/customer/${customerId}/project/${projectId}/sitegroup/${siteGroupId}/site/${row.id}/jobs`,
    [customerId, projectId, siteGroupId],
  );

  // Stable ref for the grid refresh fn — captured through the extension so
  // post-delete can refetch page 0 without storing a function in state.
  const { onGridReady: captureGridReady, refresh: refreshGrid } = useGridRefreshRef();
  // The grid's apiRef (via the extension) feeds useGridQuickFilter so the
  // stat cards can follow the committed search.
  const [dashApiRef, setDashApiRef] = useState(null);
  const [gridReady, setGridReady] = useState(false);
  const handleGridReady = useCallback(
    api => {
      captureGridReady(api);
      setDashApiRef(api.apiRef);
      setGridReady(true);
    },
    [captureGridReady],
  );
  const [, , , committedSearch] = useGridQuickFilter(dashApiRef, gridReady);

  // The composed params the grid list is currently sending, reported through
  // the extension's onParamsChange; the stat cards fetch against these.
  const [gridParams, setGridParams] = useState(null);
  const handleParamsChange = useCallback(params => setGridParams(params), []);

  const extensionParams = useMemo(() => {
    const params = {};
    if (statusFilter === 'pending') params.latest_pending = true;
    else if (statusFilter === 'complete') params.latest_pending = false;
    else if (statusFilter === 'not_started') params.has_jobs = false;
    if (photosFilter === 'yes') params.has_photos_uploaded_by_me = true;
    else if (photosFilter === 'no') params.has_photos_uploaded_by_me = false;
    if (crewFilter) params.crew = crewFilter;
    return params;
  }, [statusFilter, photosFilter, crewFilter]);

  const quickFilters = useMemo(
    () => [
      {
        key: 'jobStatus',
        label: t('projectServices.siteTable.columns.status', 'Status'),
        type: 'single',
        allLabel: t('projectServices.siteTable.filter.allStatuses'),
        options: [
          { value: 'pending', label: t('projectServices.siteTable.filter.pending') },
          { value: 'complete', label: t('projectServices.siteTable.filter.complete') },
          { value: 'not_started', label: t('projectServices.siteTable.filter.notStarted', 'Not Started') },
        ],
        value: statusFilter,
        onChange: v => setStatusFilter(v ?? null),
      },
      {
        key: 'photos',
        label: t('projectServices.siteTable.filter.allPhotos'),
        type: 'single',
        allLabel: t('projectServices.siteTable.filter.allPhotos'),
        options: [
          { value: 'yes', label: t('projectServices.siteTable.filter.hasPhotos') },
          { value: 'no', label: t('projectServices.siteTable.filter.noPhotos') },
        ],
        value: photosFilter,
        onChange: v => setPhotosFilter(v ?? null),
      },
      // The crew filter matches the site's most recent job — pending or
      // complete — so it composes with the job-status cards.
      ...(crewOptions.length
        ? [
            {
              key: 'crew',
              label: t('projectServices.siteTable.filter.crew', 'Crew'),
              type: 'single',
              allLabel: t('projectServices.siteTable.filter.allCrews', 'All Crews'),
              options: crewOptions,
              value: crewFilter,
              onChange: v => setCrewFilter(v ?? null),
            },
          ]
        : []),
    ],
    [t, statusFilter, photosFilter, crewFilter, crewOptions, setStatusFilter, setPhotosFilter, setCrewFilter],
  );

  // --- Job-progress stat cards ---
  // The cards take the exact params the grid list sends (scope, status, and
  // every dimension filter), so they always count the list the user is
  // looking at. The job-status keys (latest_pending / has_jobs) ride along
  // too — the backend stats action strips them server-side, since the cards
  // drive that dimension. Until the grid reports its params, fall back to
  // the bare scope + photos filter.
  const [jobStats, setJobStats] = useState({});
  const statsParams = useMemo(() => {
    let params;
    if (gridParams) {
      params = { ...gridParams };
    } else {
      params = { site_group_id: siteGroupId, customer_id: customerId };
      if (photosFilter === 'yes') params.has_photos_uploaded_by_me = true;
      else if (photosFilter === 'no') params.has_photos_uploaded_by_me = false;
      if (crewFilter) params.crew = crewFilter;
    }
    if (committedSearch) params.search = committedSearch;
    return toJsonApiParams(params);
  }, [gridParams, siteGroupId, customerId, photosFilter, crewFilter, committedSearch]);

  useEffect(() => {
    if (!siteGroupId) return;
    let cancelled = false;
    fetchJobSitesStats(statsParams)
      .then(data => {
        if (!cancelled) setJobStats(data);
      })
      .catch(() => {
        if (!cancelled) setJobStats({});
      });
    return () => {
      cancelled = true;
    };
  }, [siteGroupId, statsParams]);

  // Cards act as the job-status quick filter: All clears it, the rest toggle
  // their bucket.
  const toggleStatusFilter = useCallback(
    value => setStatusFilter(prev => (prev === value ? null : value)),
    [setStatusFilter],
  );
  const summaryCards = useMemo(
    () => [
      {
        label: t('general.all', 'All'),
        value: jobStats.all ?? 0,
        color: 'var(--md-primary)',
        onClick: () => setStatusFilter(null),
        active: statusFilter === null,
      },
      {
        label: t('projectServices.siteTable.filter.pending'),
        value: jobStats.pending ?? 0,
        color: 'var(--status-attention)',
        onClick: () => toggleStatusFilter('pending'),
        active: statusFilter === 'pending',
      },
      {
        label: t('projectServices.siteTable.filter.complete'),
        value: jobStats.complete ?? 0,
        color: 'var(--status-ok)',
        onClick: () => toggleStatusFilter('complete'),
        active: statusFilter === 'complete',
      },
      {
        label: t('projectServices.siteTable.filter.notStarted', 'Not Started'),
        value: jobStats.not_started ?? 0,
        color: 'var(--status-neutral)',
        onClick: () => toggleStatusFilter('not_started'),
        active: statusFilter === 'not_started',
      },
    ],
    [t, jobStats, statusFilter, setStatusFilter, toggleStatusFilter],
  );

  const statsHeader = useMemo(
    () => (
      <StatCardGroup columns={{ xs: 2, md: 4 }} sx={{ marginBottom: 'var(--page-gap)' }}>
        {summaryCards.map(card => (
          <StatCard
            key={card.label}
            title={card.label}
            value={card.value}
            color={card.color}
            onClick={card.onClick}
            active={card.active}
          />
        ))}
      </StatCardGroup>
    ),
    [summaryCards],
  );

  const handleResetExtension = useCallback(() => {
    setStatusFilter(null);
    setPhotosFilter(null);
    setCrewFilter(null);
  }, [setStatusFilter, setPhotosFilter, setCrewFilter]);

  // Render-only overrides on the job columns the extended descriptor adds.
  const fieldOverrides = useMemo(
    () => ({
      latest_pending: {
        renderCell: ({ value }) => {
          if (value == null) {
            return (
              <Typography variant="caption" color="text.secondary">
                {jobStatusLabel(value, t)}
              </Typography>
            );
          }
          return <GridStatusCell color={value ? 'warning' : 'ok'} label={jobStatusLabel(value, t)} />;
        },
      },
      latest_crew_number: {
        valueFormatter: value => (value == null ? '' : t('projectServices.dashboard.crewLabel', { crewNumber: value })),
      },
    }),
    [t],
  );

  // The per-row job shortcuts — invoices, and the job delete for anyone who
  // may. Rendered on both surfaces: inside the desktop actions column and on
  // the mobile card, where the column does not exist. The desktop cluster
  // leads with Select; on a card the whole card is the Select affordance.
  const renderJobActions = useCallback(
    (row, { withSelect }) => {
      const { latest_job_id: latestJobId, latest_pending: latestPending } = row;
      const hasJob = latestJobId != null;
      return (
        <>
          {withSelect && (
            <Button variant="contained" color="primary" size="small" onClick={() => navigate(sitePath(row))}>
              {t('actions.select', 'Select')}
            </Button>
          )}
          <Tooltip title={t('projectServices.siteTable.tooltip.changeOrder')}>
            <span>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                aria-label={t('projectServices.siteTable.tooltip.changeOrder')}
                sx={actionButtonSx}
                disabled={!hasJob || latestPending == null}
                onClick={() => {
                  dispatch(generateChangeOrderInvoice(latestJobId)).then(blob =>
                    FileSaver.saveAs(new Blob([blob]), 'change-order.pdf'),
                  );
                }}
              >
                <ChangeCircle fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t('projectServices.siteTable.tooltip.fixtureSwapInvoice')}>
            <span>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                aria-label={t('projectServices.siteTable.tooltip.fixtureSwapInvoice')}
                sx={actionButtonSx}
                disabled={!hasJob || latestPending == null}
                onClick={() => {
                  dispatch(generateFixtureSwapInvoice(latestJobId)).then(blob =>
                    FileSaver.saveAs(new Blob([blob]), 'fixture-swap.pdf'),
                  );
                }}
              >
                <DynamicFormIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          {canDelete && hasJob && (
            <Tooltip title={t('projectServices.deleteJob')}>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  aria-label={t('projectServices.deleteJob')}
                  sx={actionButtonSx}
                  onClick={() =>
                    confirmDelete(row, {
                      entityLabel: t('projectServices.job', 'Job'),
                      onConfirm: async deleted => {
                        const result = await dispatch(deleteJob(deleted.latest_job_id));
                        if (result) refreshGrid();
                      },
                    })
                  }
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          )}
        </>
      );
    },
    [dispatch, canDelete, confirmDelete, navigate, sitePath, t, refreshGrid],
  );

  // One actions column: the Select row affordance plus the invoice/delete
  // shortcuts, replacing the descriptor's own actions column (excluded via
  // extension.excludeFields). latest_job_id rides in the row payload.
  const extraColumns = useMemo(
    () => [
      {
        field: 'job_actions',
        headerName: t('general.actions', 'Actions'),
        width: 220,
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        sortable: false,
        renderCell: params => (
          <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
            {renderJobActions(params.row, { withSelect: true })}
          </Stack>
        ),
      },
    ],
    [renderJobActions, t],
  );

  // The mobile card restates the job columns as metadata and carries the same
  // row actions; a phone has no columns and no actions column to put them in.
  const getMobileSections = useCallback(
    row => ({
      meta: [
        // Job status states "None" for a site with no job yet, so it is stated
        // unconditionally rather than presence-guarded away with its field.
        {
          key: 'job_status',
          label: t('projectServices.siteTable.columns.status'),
          value: jobStatusLabel(row.latest_pending, t),
        },
        ...selectRowMeta(row, [
          { field: 'job_count', label: t('projectServices.siteTable.columns.jobs') },
          {
            field: 'latest_installation_date',
            label: t('projectServices.siteTable.columns.date'),
            format: value => formatGridDateTime(value, { dateOnly: true }),
          },
          {
            field: 'latest_crew_number',
            label: t('projectServices.siteTable.columns.lastCrew', 'Last Crew'),
            format: value => t('projectServices.dashboard.crewLabel', { crewNumber: value }),
          },
          { field: 'location_description', label: t('general.location', 'Location') },
        ]),
      ],
      actions: renderJobActions(row, { withSelect: false }),
    }),
    [renderJobActions, t],
  );

  const gridProps = useMemo(
    () => ({
      localeText: { noRowsLabel: t('projectServices.siteTable.noRows', { term: siteTerm.plural.toLowerCase() }) },
    }),
    [t, siteTerm],
  );

  const extension = useMemo(
    () => ({
      params: extensionParams,
      quickFilters,
      fieldOverrides,
      extraColumns,
      gridProps,
      // Job-progress cards replace the health cards — a crew acts on
      // pending/complete/not-started, not operational/alarming counts. The
      // base site filters move behind the More popover for the same reason.
      statsHeader,
      // The Select affordance lives in the merged job_actions column above —
      // drop the descriptor's own actions column so only one renders.
      excludeFields: EXCLUDED_DESCRIPTOR_FIELDS,
      demoteBaseQuickFilters: true,
      hideWriteActions: true,
      // extraColumns are desktop only; the card is where the same job facts
      // and row actions reach a phone.
      mobile: { getSections: getMobileSections },
      onResetAll: handleResetExtension,
      onGridReady: handleGridReady,
      onParamsChange: handleParamsChange,
    }),
    [
      extensionParams,
      quickFilters,
      fieldOverrides,
      extraColumns,
      gridProps,
      statsHeader,
      getMobileSections,
      handleResetExtension,
      handleGridReady,
      handleParamsChange,
    ],
  );

  const isLoading = customerLoading || isPlantLoading;

  return (
    <AppNav flex loading={isLoading} page={`services_projects_${customerId}_sites`} notFullHeight>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', px: 2, pb: 1 }}>
        {/* Bare term title: the plant already names itself in the breadcrumb
            trail, and TitleBar appends the title as the current-page crumb —
            a "{plant} Sites" title would put the plant in the trail twice. */}
        <TitleBar showBackButton title={isLoading && !selectedPlant ? <CircularProgress /> : siteTerm.plural} />
        {customerError || isPlantError ? (
          <FetchErrorState
            message={
              customerError
                ? t('projectServices.plantList.toast.failedToFetchCustomer', 'Failed to fetch customer')
                : t('general.scopeLoadError', "Couldn't load this page. Check your connection and try again.")
            }
            onRetry={handleScopeRetry}
          />
        ) : (
          customer &&
          selectedPlant && (
            <SiteGrid
              customer={customer}
              selectedPlant={selectedPlant}
              user={user}
              persistScope="servicesSiteGrid"
              basePath="job-sites"
              sitePath={sitePath}
              extension={extension}
            />
          )
        )}
      </Box>
      {deleteConfirmDialog}
    </AppNav>
  );
}

export default SitesGridView;
